// Conexión de anuncios de Meta por marca (Facebook Login for Business). El token
// nunca pasa por acá: lo guardan y lo leen las edges `ads-callback` y
// `ads-conexion`, y el backend. Ver ADR-2026-09-24-anuncios-de-meta-conexion.
//
// Flujo (mismo molde que `instagramConexion.ts`):
//   1. click → `abrirVentanaDeAnuncios()` (SINCRÓNICO, antes de cualquier await)
//   2. `conectarAnunciosEnVentana(brandId, popup)`:
//      ads-conexion/iniciar → login de Facebook → ads-callback deja todo en espera →
//      ads-conectado.html avisa el claim → ads-conexion/finalizar lo reclama.
//   3. Si la persona tiene varias cuentas publicitarias, `finalizar` devuelve la
//      lista y la pantalla llama a `elegirCuentaDeAnuncios(claim, id)`.

import { supabase } from "./supabase";
import { leerAvisoDeConexion, type AvisoDeConexion } from "./instagramConexionLogica";
import {
  CLAVE_AVISO_ANUNCIOS,
  ErrorDeAnuncios,
  EVENTO_CAMBIO_DE_ANUNCIOS,
  leerCuentas,
  TIPO_AVISO_ANUNCIOS,
  type CuentaDeAnuncios,
} from "./metaAdsConexionLogica";

export {
  ANUNCIOS_DE_META_PARA_TODOS,
  ErrorDeAnuncios,
  EVENTO_CAMBIO_DE_ANUNCIOS,
  mensajeDeErrorDeAnuncios,
  nombreDeCuenta,
  veAnunciosDeMeta,
  type CuentaDeAnuncios,
} from "./metaAdsConexionLogica";

/** Loguearse en Facebook y aceptar los permisos puede llevar un rato (el state vence a los 10 min). */
const ESPERA_MAXIMA_MS = 10 * 60 * 1000;

function avisarCambio() {
  try {
    window.dispatchEvent(new Event(EVENTO_CAMBIO_DE_ANUNCIOS));
  } catch {
    /* noop */
  }
}

/**
 * Abre la ventana del login. **Llamar SIEMPRE de forma sincrónica dentro del
 * handler del click**: Safari y los navegadores de iPhone la bloquean después de
 * un `await` (ADR-2026-08-07-tiendanube-popup-safari).
 */
export function abrirVentanaDeAnuncios(): Window | null {
  return window.open("/ads-conectando.html", "ads_connect", "width=620,height=760");
}

type Invocacion = Record<string, unknown>;

/** Llama a `ads-conexion` y convierte sus errores en `ErrorDeAnuncios`. */
async function invocar<T>(body: Invocacion): Promise<T> {
  const { data, error } = await supabase.functions.invoke("ads-conexion", { body });
  if (!error) return data as T;
  let cuerpo: { code?: string; marca?: string | null; cuenta?: string | null; elegir?: unknown } = {};
  try {
    cuerpo = (await (error as { context?: Response }).context?.json()) ?? {};
  } catch {
    /* sin cuerpo: error de red */
  }
  throw new ErrorDeAnuncios(
    cuerpo.code || "red",
    { marca: cuerpo.marca, cuenta: cuerpo.cuenta },
    leerCuentas(cuerpo.elegir),
  );
}

function cerrar(popup: Window | null | undefined) {
  try {
    popup?.close();
  } catch {
    /* noop */
  }
}

function olvidarAviso() {
  try {
    localStorage.removeItem(CLAVE_AVISO_ANUNCIOS);
  } catch {
    /* noop */
  }
}

function esperarAviso(popup: Window): Promise<AvisoDeConexion> {
  return new Promise((resolve, reject) => {
    let listo = false;
    const leer = (d: unknown) => leerAvisoDeConexion(d, Date.now(), TIPO_AVISO_ANUNCIOS);

    const terminar = (aviso: AvisoDeConexion | null, err?: ErrorDeAnuncios) => {
      if (listo) return;
      listo = true;
      window.removeEventListener("message", alMensaje);
      window.removeEventListener("storage", alStorage);
      clearInterval(vigia);
      clearTimeout(limite);
      olvidarAviso();
      if (aviso) resolve(aviso);
      else reject(err);
    };

    const alMensaje = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const aviso = leer(e.data);
      if (aviso) terminar(aviso);
    };
    const alStorage = (e: StorageEvent) => {
      if (e.key !== CLAVE_AVISO_ANUNCIOS) return;
      const aviso = leer(e.newValue);
      if (aviso) terminar(aviso);
    };
    window.addEventListener("message", alMensaje);
    window.addEventListener("storage", alStorage);

    const vigia = setInterval(() => {
      if (!popup.closed) return;
      let aviso: AvisoDeConexion | null = null;
      try {
        aviso = leer(localStorage.getItem(CLAVE_AVISO_ANUNCIOS));
      } catch {
        aviso = null;
      }
      terminar(aviso, aviso ? undefined : new ErrorDeAnuncios("ventana_cerrada"));
    }, 700);

    const limite = setTimeout(() => {
      cerrar(popup);
      terminar(null, new ErrorDeAnuncios("tiempo"));
    }, ESPERA_MAXIMA_MS);
  });
}

export type ResultadoDeConexion =
  | { tipo: "conectada"; cuenta: CuentaDeAnuncios }
  /** Hay varias cuentas: la pantalla muestra la lista y llama a `elegirCuentaDeAnuncios`. */
  | { tipo: "elegir"; claim: string; cuentas: CuentaDeAnuncios[] };

async function finalizar(claim: string, adAccountId?: string): Promise<ResultadoDeConexion> {
  const r = await invocar<{ conectada?: boolean; cuenta?: unknown; elegir?: unknown }>({
    accion: "finalizar",
    claim,
    ...(adAccountId ? { ad_account_id: adAccountId } : {}),
  });
  if (r.elegir) return { tipo: "elegir", claim, cuentas: leerCuentas(r.elegir) };
  const [cuenta] = leerCuentas([r.cuenta]);
  avisarCambio();
  return { tipo: "conectada", cuenta };
}

/**
 * Conecta los anuncios de una marca sobre una ventana **ya abierta por el click**
 * (`abrirVentanaDeAnuncios`). Tira `ErrorDeAnuncios` con un mensaje listo para mostrar.
 */
export async function conectarAnunciosEnVentana(
  brandId: string | null,
  popup: Window | null | undefined,
): Promise<ResultadoDeConexion> {
  if (!popup) throw new ErrorDeAnuncios("popup_bloqueado");
  olvidarAviso();

  let url: string;
  try {
    ({ url } = await invocar<{ url: string }>({ accion: "iniciar", brand_id: brandId }));
  } catch (e) {
    cerrar(popup);
    throw e;
  }
  if (popup.closed) throw new ErrorDeAnuncios("ventana_cerrada");
  popup.location.href = url;

  const aviso = await esperarAviso(popup);
  cerrar(popup);
  if ("error" in aviso) throw new ErrorDeAnuncios(aviso.error);
  return finalizar(aviso.claim);
}

/** Termina la conexión con la cuenta que eligió la persona (de la lista que devolvió Meta). */
export function elegirCuentaDeAnuncios(claim: string, adAccountId: string): Promise<ResultadoDeConexion> {
  return finalizar(claim, adAccountId);
}

export interface EstadoDeAnuncios {
  conectada: boolean;
  /** activa | vencida | revocada; null si nunca se conectó. */
  estado: string | null;
  cuenta: { id: string; nombre: string | null; negocio: string | null } | null;
  venceAt: string | null;
  /** `true` = no pudimos preguntar: `conectada: false` quiere decir "no sabemos". */
  error: boolean;
}

/** Estado de la conexión de una marca. Liviano: no le pega a Meta. */
export async function estadoDeAnuncios(brandId: string | null): Promise<EstadoDeAnuncios> {
  try {
    const d = await invocar<{
      conectada?: boolean;
      estado?: string;
      cuenta?: { id: string; nombre: string | null; negocio: string | null } | null;
      vence_at?: string | null;
    }>({ accion: "estado", brand_id: brandId });
    return {
      conectada: !!d?.conectada,
      estado: d?.estado ?? null,
      cuenta: d?.cuenta ?? null,
      venceAt: d?.vence_at ?? null,
      error: false,
    };
  } catch {
    return { conectada: false, estado: null, cuenta: null, venceAt: null, error: true };
  }
}

/** Desconecta los anuncios de una marca (borra la conexión y el token). */
export async function desconectarAnuncios(brandId: string | null): Promise<void> {
  await invocar({ accion: "desconectar", brand_id: brandId });
  avisarCambio();
}
